import { IAntDProTableRequest } from "../../../general/type/general.types";
import { apiRequest, downloadFile } from "../../../services/api";
import { openOrDownloadBlob } from "../../../utils/fileDownload";
import {
  ILeaveRequest,
  IApiResponseGetLeaves,
  IUpdateLeaveStatusRequest,
  IApiResponseGetUserLeaveBalanceByType,
} from "../type/leave";

export const getLeaves = async (
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetLeaves> => {
  const response = await apiRequest("GET", "/leaves", tableRequest);
  return response.data;
};

export const getUserLeaves = async (
  userId: string,
  tableRequest: IAntDProTableRequest
): Promise<IApiResponseGetLeaves> => {
  const response = await apiRequest(
    "GET",
    `/leaves/user-leaves/${userId}`,
    tableRequest
  );
  return response.data;
};

export const createLeave = async (data: ILeaveRequest, file?: File) => {
  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("leaveType", data.leaveType);
    formData.append("startDate", data.startDate.toISOString());
    formData.append("endDate", data.endDate.toISOString());
    if (data.totalDays) {
      formData.append("totalDays", data.totalDays.toString());
    }
    if (data.reason) {
      formData.append("reason", data.reason);
    }

    const response = await apiRequest("POST", `/leaves`, formData, true);
    return response.data;
  } else {
    const payload = {
      leaveType: data.leaveType,
      startDate: data.startDate,
      endDate: data.endDate,
      ...(data.totalDays && { totalDays: data.totalDays }),
      ...(data.reason && { reason: data.reason }),
    };
    const response = await apiRequest<ILeaveRequest>(
      "POST",
      "/leaves",
      payload
    );
    return response.data;
  }
};

export const getLeaveById = async (id: string | undefined) => {
  const response = await apiRequest<IApiResponseGetLeaves>(
    "GET",
    `/leaves/${id}`
  );
  return response.data;
};

export const editLeave = async (
  id: string,
  data: ILeaveRequest,
  file?: File
) => {
  if (file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("leaveType", data.leaveType);
    formData.append("startDate", data.startDate.toISOString());
    formData.append("endDate", data.endDate.toISOString());
    if (data.totalDays) {
      formData.append("totalDays", data.totalDays.toString());
    }
    if (data.reason) {
      formData.append("reason", data.reason);
    }

    const response = await apiRequest("PATCH", `/leaves/${id}`, formData, true);
    return response.data;
  } else {
    const payload = {
      ...(data.leaveType && { leaveType: data.leaveType }),
      ...(data.startDate && { startDate: data.startDate }),
      ...(data.endDate && { endDate: data.endDate }),
      ...(data.totalDays && { totalDays: data.totalDays }),
      ...(data.reason && { reason: data.reason }),
    };

    const response = await apiRequest<ILeaveRequest>(
      "PATCH",
      `/leaves/${id}`,
      payload
    );
    return response.data;
  }
};

export const updateLeaveStatus = async (
  id: string,
  data: IUpdateLeaveStatusRequest
) => {
  const payload = {
    status: data.status,
  };
  const response = await apiRequest<IUpdateLeaveStatusRequest>(
    "PATCH",
    `/leaves/${id}/status`,
    payload
  );
  return response.data;
};

export const archiveLeave = async (id: string) => {
  const response = await apiRequest("PATCH", `/leaves/${id}/archive`);
  return response.data;
};

export const getUserLeavesBalanceByType = async (
  leaveType: string
): Promise<IApiResponseGetUserLeaveBalanceByType> => {
  const response = await apiRequest(
    "GET",
    `/leaves/user-leave-balance/${leaveType}`
  );
  return response.data;
};

export const downloadLeaveFileFromS3 = async (
  leaveId: string,
  relativePath: string
) => {
  try {
    const blob = await downloadFile(
      `/leaves/${leaveId}/download?filePath=${encodeURIComponent(relativePath)}`
    );
    const fileName = relativePath.split("/").pop() || "downloaded-file";
    openOrDownloadBlob(blob, fileName);
    return true;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

export const checkOverlappingLeaves = async (
  startDate: string | Date,
  endDate: string | Date,
  leaveId?: string
): Promise<{ hasOverlap: boolean }> => {
  const payload = {
    startDate,
    endDate,
    ...(leaveId && { leaveId }),
  };
  const response = await apiRequest("POST", "/leaves/check-overlap", payload);
  return response.data;
};
